import {isFeatureEnabled} from '@github-ui/feature-flags'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {IssueFeedbackDialog, type IssueFeedbackDialogRef} from '@github-ui/issue-viewer/IssueFeedbackDialog'
import {JobInfoWithSubscription} from '@github-ui/issues-bulk-actions/JobInfoWithSubscription'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {Link} from '@primer/react'
import {useEffect, useRef} from 'react'
import {
  type EntryPointComponent,
  graphql,
  type PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
} from 'react-relay'

import {CallToActionItem} from '../components/CallToActionItem'
import {FirstTimeContributionBanner} from '../components/firstTimeContributionBanner/FirstTimeContributionBanner'
import {PinnedIssues} from '../components/pinnedIssues/PinnedIssues'
import MobileNavigation from '../components/sidebar/MobileNavigation'
import {RepoIssuesSidebar} from '../components/sidebar/RepoIssuesSidebar'
import {StaleCacheBanner} from '../components/StaleCacheBanner'
import {useQueryBulk, useQueryContext} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {
  IssueRepoNewChoosePage,
  IssueRepoNewPage,
  IssueViewsPage,
  RepositoryLabelIndexPage,
  RepositoryMilestoneIndexPage,
  RepositoryMilestonePage,
} from '../lazy-routes'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import type {IssueIndexPageQuery, IssueIndexPageQuery$variables} from './__generated__/IssueIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'
import styles from './IssueIndexPage.module.css'
import {List} from './List'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueIndexPageQuery(
    $query: String = "state:open archived:false assignee:@me sort:updated-desc"
    $first: Int = 25
    $labelPageSize: Int = 20
    $skip: Int = null
    $name: String!
    $owner: String!
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      owner {
        userNameDisplayConfiguration
      }
      ...PinnedIssues
      ...ListRepositoryFragment
      ...FirstTimeContributionBanner
      ...ListQuery
        @arguments(query: $query, first: $first, labelPageSize: $labelPageSize, skip: $skip, fetchRepository: false)
    }
    safeViewer {
      # this is intentional to have the current viewer preloaded for the item pickers
      __typename
      id
      login
      name
      profileResourcePath
      avatarUrl(size: 64)
    }
  }
`

export const IssueIndexPage: EntryPointComponent<{pageQuery: IssueIndexPageQuery}, Record<string, never>> = ({
  queries: {pageQuery},
}) => {
  const {queryRef, loadQuery} = useEntryPointsLoader(pageQuery, PageQuery)

  const {setCurrentViewId} = useQueryContext()

  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQuery, setCurrentViewId])

  if (!queryRef) return null

  return <IssueIndexPageContent pageQueryRef={queryRef} loadQuery={loadQuery} />
}

function IssueIndexPageContent({
  pageQueryRef,
  loadQuery,
}: {
  pageQueryRef: PreloadedQuery<IssueIndexPageQuery>
  loadQuery: (variables: IssueIndexPageQuery$variables, options?: UseQueryLoaderLoadQueryOptions | undefined) => void
}) {
  const currentViewNode = useLazyLoadQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    {id: VIEW_IDS.repository},
    {fetchPolicy: 'store-only'},
  )

  useEffect(() => {
    RepositoryMilestoneIndexPage.preload()
    RepositoryLabelIndexPage.preload()
    RepositoryMilestonePage.preload()
    IssueRepoNewPage.preload()
    IssueRepoNewChoosePage.preload()
    if (isFeatureEnabled('issue_pinned_views_index_page')) {
      IssueViewsPage.preload()
    }
  }, [])

  const {bulkJobId, setBulkJobId, triggerBulkJobSuccess} = useQueryBulk()
  const feedbackDialogRef = useRef<IssueFeedbackDialogRef>(null)
  const showFeedback = isFeatureEnabled('issues_react_ui_feedback') && !isFeatureEnabled('issues_index_semantic_search')
  const issuePinnedViewsEnabled = isFeatureEnabled('issue_pinned_views')

  const pageData = usePreloadedQuery<IssueIndexPageQuery>(PageQuery, pageQueryRef)
  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  const repository = okResult.value

  return (
    <AnalyticsWrapper category="Issues Index">
      <StaleCacheBanner />
      <UserNameDisplayContextProvider value={repository.owner.userNameDisplayConfiguration}>
        <JobInfoWithSubscription bulkJobId={bulkJobId} setBulkJobId={setBulkJobId} onSuccess={triggerBulkJobSuccess}>
          <ThreePanesLayout
            contentAs="div"
            resizeable={false}
            leftPaneWidth="medium"
            {...(issuePinnedViewsEnabled
              ? {
                  leftPane: {
                    element: <RepoIssuesSidebar />,
                    ariaLabel: 'Issues sidebar navigation',
                  },
                }
              : {})}
            middlePane={
              <>
                <div className={styles.middlePaneGrid}>
                  <CallToActionItem />
                  <FirstTimeContributionBanner repository={repository} />
                  <PinnedIssues repository={repository} />
                  {currentViewNode.node ? (
                    <List
                      fetchedView={currentViewNode.node}
                      fetchedRepository={repository}
                      search={repository}
                      loadSearchQuery={loadQuery}
                    />
                  ) : null}
                </div>
                {showFeedback && (
                  <>
                    <div className={styles.feedbackLinkContainer}>
                      <Link as="button" inline muted onClick={() => feedbackDialogRef.current?.openDialog()}>
                        Give feedback
                      </Link>
                    </div>
                    <IssueFeedbackDialog ref={feedbackDialogRef} />
                  </>
                )}
              </>
            }
          />
          <MobileNavigation />
        </JobInfoWithSubscription>
      </UserNameDisplayContextProvider>
    </AnalyticsWrapper>
  )
}
